/*
  Header search (Phase 0 rebuild, 29 Sep 2026)
  ------------------------------------------------------------------
  Replaces the previous implementation that fetched the full /search
  results section (~400 KB per keystroke) with a "*" wildcard appended.
  Now:
    - Uses Shopify's Predictive Search API via section rendering
      (/search/suggest?...&section_id=predictive-search): products,
      collections and query suggestions, typo tolerant, no wildcard.
    - Aborts in-flight requests when the query changes (fixes the
      intermittent "No results found" race under fast typing).
    - Shows popular searches, recent searches and quick categories when
      the box is focused while empty.
    - Keeps the search term in the box on the results page.
    - Sends a GA4 "search" event (search_source = header_dropdown) and a
      Shopify custom pixel event so dropdown searches become measurable.
  DOM ids/classes from the original are preserved so header-search.css
  keeps working (Header__SearchWrapper, Header__SearchResults,
  Header--search-open, mobile-search-active, HeaderSearchOverlay, ...).
*/

// Set header height CSS variable
(function () {
  var headerSection = document.getElementById('shopify-section-header');
  if (headerSection) {
    document.documentElement.style.setProperty('--header-height', headerSection.offsetHeight + 'px');
  }
})();

(function () {
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 220;
  var RECENT_KEY = 'tfg_recent_searches';
  var RECENT_MAX = 5;

  function initHeaderSearch() {
    var searchInput = document.getElementById('HeaderSearchInput');
    var searchResults = document.getElementById('HeaderSearchResults');
    var searchCancel = document.getElementById('HeaderSearchCancel');
    var searchWrapper = document.querySelector('.Header__SearchWrapper');
    var mobileSearchIcon = document.getElementById('MobileSearchIconBtn');
    var searchOverlay = document.getElementById('HeaderSearchOverlay');
    var popularTemplate = document.getElementById('HeaderSearchPopular');

    if (!searchInput || !searchResults || !searchWrapper) return;

    var isMobileSearchActive = false;
    var lastQuery = '';
    var lastTrackedQuery = '';
    var controller = null;
    var debounceTimer = null;
    var cache = {};

    /* ---------- helpers ---------- */

    function isDesktop() {
      return window.innerWidth >= 768;
    }

    function getHeader() {
      var section = document.querySelector('.shopify-section--header');
      return section ? section.querySelector('header') : null;
    }

    function lockBodyScroll() {
      if (isDesktop()) {
        document.body.classList.add('search-open');
        document.documentElement.classList.add('search-open');
      }
    }

    function unlockBodyScroll() {
      document.body.classList.remove('search-open');
      document.documentElement.classList.remove('search-open');
    }

    function updateSearchResultsPosition() {
      var header = getHeader();
      if (!header) return;
      void header.offsetHeight;
      var headerHeight = header.getBoundingClientRect().height;
      var wrapper = header.querySelector('.Header__Wrapper');
      if (wrapper) headerHeight = Math.max(headerHeight, wrapper.getBoundingClientRect().height);
      searchResults.style.top = headerHeight + 'px';
      searchResults.style.maxHeight = 'calc(100vh - ' + headerHeight + 'px)';
      document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
      if (searchOverlay && searchResults.getAttribute('aria-hidden') === 'false') {
        searchOverlay.style.top = searchResults.getBoundingClientRect().bottom + 'px';
      }
    }

    function openPanel() {
      var header = getHeader();
      if (header) header.classList.add('Header--search-open');
      if (isDesktop()) {
        searchWrapper.classList.add('is-active');
      } else if (!searchWrapper.classList.contains('mobile-search-active')) {
        searchWrapper.classList.add('mobile-search-active');
        isMobileSearchActive = true;
      }
      searchResults.setAttribute('aria-hidden', 'false');
      searchInput.setAttribute('aria-expanded', 'true');
      if (searchOverlay && isDesktop()) {
        searchOverlay.setAttribute('aria-hidden', 'false');
      }
      lockBodyScroll();
      requestAnimationFrame(function () {
        updateSearchResultsPosition();
        setTimeout(updateSearchResultsPosition, 30);
      });
    }

    function closePanelOnly() {
      searchResults.setAttribute('aria-hidden', 'true');
      searchInput.setAttribute('aria-expanded', 'false');
      searchWrapper.classList.remove('is-active');
      if (searchOverlay) searchOverlay.setAttribute('aria-hidden', 'true');
      var header = getHeader();
      if (header) header.classList.remove('Header--search-open');
      unlockBodyScroll();
    }

    function resetSearch() {
      if (controller) { controller.abort(); controller = null; }
      clearTimeout(debounceTimer);
      searchResults.innerHTML = '';
      closePanelOnly();
      searchWrapper.classList.remove('mobile-search-active');
      isMobileSearchActive = false;
      document.body.classList.remove('mobile-search-open');
      document.documentElement.style.setProperty('--mobile-search-height', '0px');
      if (mobileSearchIcon) mobileSearchIcon.setAttribute('aria-expanded', 'false');
      // Keep the typed term on the search results page so users can refine it.
      if (document.body.classList.contains('template-search') === false) {
        searchInput.value = '';
      }
      lastQuery = '';
      updateCancelButton();
    }

    function updateCancelButton() {
      if (!searchCancel) return;
      searchCancel.style.display = searchInput.value.trim() !== '' ? 'block' : 'none';
    }

    function setResults(html) {
      searchResults.innerHTML = html;
      openPanel();
    }

    /* ---------- recent searches (per-browser convenience only) ---------- */

    function getRecent() {
      try {
        var raw = window.localStorage.getItem(RECENT_KEY);
        var list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
      } catch (e) { return []; }
    }

    function addRecent(term) {
      try {
        var list = getRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
        list.unshift(term);
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
      } catch (e) { /* storage unavailable: ignore */ }
    }

    function escapeHtml(str) {
      return String(str).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    /* ---------- empty state: popular + recent + categories ---------- */

    function renderEmptyState() {
      var html = '<div class="Segment PredictiveSearch PredictiveSearch--empty"><div class="PredictiveSearch__Layout PredictiveSearch__Layout--empty">';
      var recent = getRecent();
      if (recent.length) {
        html += '<div class="PredictiveSearch__Block"><p class="PredictiveSearch__SideTitle Heading Text--subdued u-h7">Recent searches</p><ul class="PredictiveSearch__Chips">';
        recent.forEach(function (t) {
          html += '<li><button type="button" class="PredictiveSearch__Chip" data-fill-query="' + escapeHtml(t) + '">' + escapeHtml(t) + '</button></li>';
        });
        html += '</ul></div>';
      }
      if (popularTemplate) {
        html += popularTemplate.innerHTML;
      }
      html += '</div></div>';
      setResults(html);
    }

    /* ---------- tracking ---------- */

    // GA4 is loaded on this store by Shopify's Google & YouTube channel pixel
    // (runs in the page context and processes the global dataLayer). Pushing a
    // gtag-style "arguments" entry lets that instance send the event under the
    // existing G- measurement ID without loading a second copy of gtag.js.
    function sendGa4Event(name, params) {
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', name, params);
          return;
        }
        window.dataLayer = window.dataLayer || [];
        (function () { window.dataLayer.push(arguments); })('event', name, params);
      } catch (e) { /* ignore */ }
    }

    function trackSearch(query, count) {
      if (!query || query === lastTrackedQuery) return;
      lastTrackedQuery = query;
      var payload = { search_term: query, search_source: 'header_dropdown', results_count: count };
      sendGa4Event('search', payload);
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('tfg_search_dropdown', payload);
        }
      } catch (e) { /* ignore */ }
    }

    /* ---------- predictive search request ---------- */

    function buildUrl(query) {
      var base = (window.routes && window.routes.predictiveSearchUrl) ? window.routes.predictiveSearchUrl : '/search/suggest';
      var params = [
        'q=' + encodeURIComponent(query),
        'resources[type]=product,collection,query',
        'resources[limit]=10',
        'resources[limit_scope]=each',
        'resources[options][unavailable_products]=last',
        'resources[options][fields]=title,product_type,variants.title,vendor,tag',
        'section_id=predictive-search'
      ];
      return base + '?' + params.join('&');
    }

    function performSearch(rawQuery) {
      var query = rawQuery.trim();
      if (query.length < MIN_CHARS) {
        if (query.length === 0) {
          renderEmptyState();
        }
        return;
      }
      lastQuery = query;

      if (cache[query]) {
        handleResponse(query, cache[query]);
        return;
      }

      if (controller) controller.abort();
      controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;

      fetch(buildUrl(query), {
        method: 'GET',
        credentials: 'same-origin',
        signal: controller ? controller.signal : undefined
      })
        .then(function (response) { return response.text(); })
        .then(function (html) {
          if (lastQuery !== query) return; // stale response
          cache[query] = html;
          handleResponse(query, html);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (lastQuery !== query) return;
          setResults('<div class="Segment PredictiveSearch"><div class="PredictiveSearch__Empty"><p class="PredictiveSearch__EmptyText Text--subdued">Search is taking longer than usual. Press Enter to see all results.</p></div></div>');
        });
    }

    function handleResponse(query, html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var root = doc.querySelector('[data-predictive-search]');
      var count = 0;
      if (root) {
        count = parseInt(root.getAttribute('data-product-count') || '0', 10) || 0;
        setResults(root.outerHTML); // keep the .Segment.PredictiveSearch wrapper (padding/layout)
      } else {
        var section = doc.querySelector('.shopify-section') || doc.body;
        setResults(section ? section.innerHTML : '');
      }
      trackSearch(query, count);
    }

    /* ---------- events ---------- */

    if (mobileSearchIcon) {
      mobileSearchIcon.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (isDesktop()) return;
        isMobileSearchActive = !isMobileSearchActive;
        if (isMobileSearchActive) {
          searchWrapper.classList.add('mobile-search-active');
          mobileSearchIcon.setAttribute('aria-expanded', 'true');
          document.body.classList.add('mobile-search-open');
          setTimeout(function () {
            document.documentElement.style.setProperty('--mobile-search-height', searchWrapper.offsetHeight + 'px');
          }, 10);
          setTimeout(function () { searchInput.focus(); }, 100);
        } else {
          resetSearch();
        }
      });
    }

    searchInput.addEventListener('input', function () {
      updateCancelButton();
      clearTimeout(debounceTimer);
      var value = searchInput.value;
      debounceTimer = setTimeout(function () { performSearch(value); }, DEBOUNCE_MS);
    });

    searchInput.addEventListener('focus', function () {
      updateSearchResultsPosition();
      var value = searchInput.value.trim();
      if (value.length >= MIN_CHARS) {
        if (searchResults.innerHTML.trim() !== '' && lastQuery === value) {
          openPanel();
        } else {
          performSearch(value);
        }
      } else {
        renderEmptyState();
      }
      updateCancelButton();
    });

    searchInput.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        resetSearch();
        searchInput.blur();
      }
    });

    // Clicks inside the results: recent-search chips fill the box
    searchResults.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-fill-query]');
      if (chip) {
        e.preventDefault();
        searchInput.value = chip.getAttribute('data-fill-query');
        updateCancelButton();
        performSearch(searchInput.value);
        searchInput.focus();
        return;
      }
      var suggestion = e.target.closest('[data-suggestion]');
      if (suggestion) {
        addRecent(suggestion.getAttribute('data-suggestion'));
      }
    });

    if (searchCancel) {
      searchCancel.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        resetSearch();
      });
    }

    document.addEventListener('click', function (event) {
      if (mobileSearchIcon && mobileSearchIcon.contains(event.target)) return;
      if (searchWrapper && !searchWrapper.contains(event.target)) {
        if (searchResults.getAttribute('aria-hidden') === 'false' || searchWrapper.classList.contains('mobile-search-active')) {
          resetSearch();
        }
      }
    });

    if (searchOverlay) {
      searchOverlay.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        resetSearch();
      });
    }

    var searchForm = searchInput.closest('form');
    if (searchForm) {
      searchForm.addEventListener('submit', function (e) {
        var value = searchInput.value.trim();
        if (value === '') {
          e.preventDefault();
          return false;
        }
        searchInput.value = value; // never append wildcards
        addRecent(value);
        // The results page itself fires GA4's automatic view_search_results;
        // no extra event needed here.
      });
    }

    window.addEventListener('scroll', function () {
      if (searchResults.getAttribute('aria-hidden') === 'false') updateSearchResultsPosition();
    }, { passive: true });

    window.addEventListener('resize', function () {
      if (searchResults.getAttribute('aria-hidden') === 'false') updateSearchResultsPosition();
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderSearch);
  } else {
    initHeaderSearch();
  }
})();
